import React from 'react';
import { hot } from 'react-hot-loader/root';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import selectSortedGameCards from '../selectors/sorted-game-cards';
import AccountLayout from '../layouts/AccountLayout';
import GameCards from '../components/GameCards';
import PageSection from '../components/PageSection';
import ProgressBar from '../components/ProgressBar';
import {
  ssColorWhite,
  mediaBreakpointUpSM,
  mediaBreakpointUpMD,
  mediaBreakpointUpLG,
  ssColorCyan,
} from '../constants/style-constants';
import { vunit, hunit } from '../lib/style-helpers';
import {
  Header3, Paragraph, DisplayHeader, Header2,
} from '../components/Typography';
import phrasesTypes from '../proptypes/phrases';
import selectPlayableChallengeCounts from '../selectors/playable-challenge-counts';

const headerCss = css`
  color: ${ssColorWhite};
  display: flex;
  flex-direction: column;
  padding-left: ${hunit(1)};
  padding-right: ${hunit(1)};
  text-align: center;

  ${mediaBreakpointUpSM} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const headerTitleCss = css`
  color: ${ssColorCyan};
  display: block;
  font-size: 2rem;
  margin-bottom: ${vunit(5)};
  order: 2;
  text-align: center;
  word-break: break-word;

  ${mediaBreakpointUpSM} {
    font-size: 2.4rem;
    margin-bottom: ${vunit(6)};
  }

  ${mediaBreakpointUpLG} {
    font-size: 3rem;
  }
`;

const headerSubTitleCss = css`
  font-size: 0.8rem;
  margin-bottom: ${vunit(1)};
  order: 1;
  padding-left: ${hunit(1)};
  padding-right: ${hunit(1)};

  ${mediaBreakpointUpSM} {
    padding-left: 0;
    padding-right: 0;
  }

  ${mediaBreakpointUpMD} {
    font-size: 0.9rem;
  }

  ${mediaBreakpointUpLG} {
    font-size: 1.1rem;
  }
`;

const Header = ({ pageTitle, pageSubTitle }) => (
  <div css={headerCss}>
    <DisplayHeader css={headerTitleCss}>{pageTitle}</DisplayHeader>
    <Header2 css={headerSubTitleCss}>{pageSubTitle}</Header2>
  </div>
);
Header.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  pageSubTitle: PropTypes.string.isRequired,
};

const returnPromptCss = css`
  text-align: center;
`;

const returnPromptHeadingCss = css`
  font-size: 1.2rem;
`;

const ReturnPrompt = ({ title, message }) => (
  <aside css={returnPromptCss}>
    <Header3 css={returnPromptHeadingCss}>{title}</Header3>
    <Paragraph>{message}</Paragraph>
  </aside>
);
ReturnPrompt.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
};

const cardsGridCss = css`
  margin-bottom: ${vunit(4)};

  ${mediaBreakpointUpLG} {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: ${hunit(1.5)};
    margin-bottom: ${vunit(6)};
  }
`;

const progressBarCss = css`
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${vunit(5)};
  max-width: 450px;
  padding: 0 ${hunit(2)};
  width: 100%;

  ${mediaBreakpointUpMD} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const GamesDashboardPage = ({
  progress: {
    games,
    displayOrder: gamesDisplayOrder,
  },
  phrases: {
    games: gamesPhrases,
    returnPrompt: returnPromptPhrases,
    gamesProgress: gamesProgressPhrases,
  },
  phrases,
}) => {
  const {
    total: totalChallenges,
    remaining: challengesRemaining,
  } = selectPlayableChallengeCounts(games);
  const progressBarCount = totalChallenges - challengesRemaining;

  return (
    <AccountLayout>
      <PageSection theme="dark_navy">
        <div css={progressBarCss}>
          <ProgressBar
            label={gamesProgressPhrases.progressBarLabel}
            total={totalChallenges}
            count={progressBarCount}
          />
        </div>
        <Header pageTitle={phrases.pageTitle} pageSubTitle={phrases.pageSubTitle} />
        <div className="row">
          <div className="col-12">
            <div css={[cardsGridCss]} data-testid="game-cards">
              <GameCards games={selectSortedGameCards(gamesPhrases, gamesDisplayOrder, games)} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <ReturnPrompt
              title={returnPromptPhrases.title}
              message={returnPromptPhrases.message}
            />
          </div>
        </div>
      </PageSection>
    </AccountLayout>
  );
};

GamesDashboardPage.propTypes = {
  progress: PropTypes.shape({
    games: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      remaining: PropTypes.number.isRequired,
      path: PropTypes.string.isRequired,
    })),
    displayOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  phrases: PropTypes.shape({
    games: phrasesTypes,
    gamesProgress: PropTypes.shape({
      progressBarLabel: PropTypes.string.isRequired,
    }),
    pageTitle: PropTypes.string.isRequired,
    pageSubTitle: PropTypes.string.isRequired,
    returnPrompt: PropTypes.shape({
      title: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default hot(GamesDashboardPage);
